import { DataGrid } from '@mui/x-data-grid';
import { Button, CircularProgress, Typography } from '@mui/material';
import { useMemo, useState, } from 'react';
import { DataGridStyle, CustomStartButton } from '../../shared/sharedStyles';
import { useIsOnMobile } from '../../hooks/layoutHooks';
import DownloadIcon from '@mui/icons-material/Download';
import { CoursesListForMobile } from "./CoursesTableForMobile"
import { handleCertificateDownload } from "./CoursesTableUtils"
import { useCoursesCertificatesStatusMap } from "./CoursesTableUtils"
import "../../pages/CoursesMain.css"

export const CoursesTableWrapper = (props) => {
    const isMobile = useIsOnMobile()
    if (isMobile) {
        return <CoursesListForMobile {...props} />
    } else {
        return <CoursesTableForDesktop {...props} />
    }
}

const CoursesTableForDesktop = ({
    lngsltd, userRole, data, filteredData, handleCourseClick, handleEdit, handleDelete,
    handleDetails, handleReport, buttonRef, handleStart, userid, userfname, userlname
}) => {

    const [loading, setLoading] = useState(true); // Loading state
    const { certificateStatusMap } = useCoursesCertificatesStatusMap(data, userid)

    const columns = useMemo(() => {
        return [
            {
                field: 'courseName',
                headerName: lngsltd["Course Name"],
                flex: 1,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) => (
                    userRole === 'Admin' ? (
                        <span
                            style={{ textDecoration: 'underline', cursor: 'pointer' }}
                            onClick={() => handleCourseClick(params.row.courseID)}

                        >
                            {params.value}
                        </span>
                    ) : (
                        <span>{params.value}</span>
                    )
                )
            },
            {
                field: 'description',
                headerName: lngsltd["Course Description"],
                flex: 1,
                headerAlign: 'center',
                align: 'center'
            },
            ...(userRole !== 'Admin' ? [{
                field: 'completionDuration',
                headerName: lngsltd["Completion Duration"],
                flex: 1,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) => (
                    <span>{params.value ? params.value : 'N/A'}</span>
                )
            }] : []),
            ...(userRole === 'Admin' ? [{
                field: 'groupName',
                headerName: lngsltd["Course Group"],
                flex: 1,
                headerAlign: 'center',
                align: 'center'
            }] : []),
            {
                field: 'actions',
                headerName: lngsltd["Actions"],
                flex: 2,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) => {
                    const isCompleted = params.row.completionDuration && params.row.completionDuration !== "Not Completed";
                    return userRole === 'Admin' ? (
                        <>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleEdit(params.row.courseID)}
                                sx={{ 
                                    mr: 1, 
                                    borderRadius: 1.5,
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                }}
                            >
                                {lngsltd["Edit"]}
                            </Button>
                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={() => handleDelete(params.row.courseID)}
                                sx={{ 
                                    mr: 1, 
                                    borderRadius: 1.5,
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                }}
                            >
                                {lngsltd["Delete"]}
                            </Button>
                            <Button
                                variant="outlined"
                                color="info"
                                size="small"
                                onClick={() => handleDetails(params.row.courseID)}
                                sx={{ 
                                    mr: 1, 
                                    borderRadius: 1.5,
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                }}
                            >
                                {lngsltd["Details"]}
                            </Button>
                            <Button
                                variant="outlined"
                                color="warning"
                                size="small"
                                onClick={() => handleReport(params.row.courseID)}
                                sx={{ 
                                    borderRadius: 1.5,
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                }}
                            >
                                {lngsltd["Report"]}
                            </Button>
                        </>
                    ) : (
                        <>
                            <CustomStartButton
                                ref={buttonRef}
                                onClick={() => handleStart(params.row.courseID)}
                                sx={{
                                    px: 2,
                                    py: 0.5,
                                    minWidth: 'auto',
                                }}>
                                <Typography variant="caption" fontSize="12px" fontWeight={600}>
                                    {isCompleted ? (lngsltd["Completed"] || "Completed") : lngsltd["Start"]}
                                </Typography>
                            </CustomStartButton>

                            {certificateStatusMap[params.row.courseID] && (
                                <CustomStartButton
                                    onClick={() => handleCertificateDownload(params.row.courseID, userid, userfname, userlname)}
                                    sx={{
                                        px: 2,
                                        py: 0.5,
                                        minWidth: 'auto',
                                        ml: 1,
                                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                                        },
                                    }}
                                >
                                    <Typography variant="caption" fontSize="12px" fontWeight={600}>
                                        {lngsltd["Certificate"]}
                                    </Typography>
                                    <DownloadIcon sx={{ fontSize: 16, ml: 0.5 }} />
                                </CustomStartButton>
                            )}
                        </>
                    )
                }
            }
        ]
    }, [userRole, certificateStatusMap, buttonRef]);

    const rows = useMemo(() => filteredData ? filteredData.map((d, i) => ({
        ...d,
        id: i + 1,
        courseID: d.courseID,
        courseName: d.courseName,
        description: d.description,
        completionDuration: d.completionDuration,
        groupName: d.groupName
    })) : [], [filteredData]);


    if (!data) {
        return (
            <Typography variant="h6" align="center" sx={{ paddingTop: '25px' }}>
                <CircularProgress />
            </Typography>
        );
    }
    return (
        <DataGrid
            rows={rows}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5, 10, 25]}
            disableSelectionOnClick
            disableColumnResize
            sx={DataGridStyle}
            getRowClassName={(params) => {
                // Check if completionDuration is not "Not Completed" or if you want to apply color to the row use 'row-with-completion-duration'
                return params.row.completionDuration && params.row.completionDuration !== "Not Completed"
                    ? 'font-color-completion'
                    : ''; 
                    
            }}
        />
    );
}